import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Wrench, Home, Building2, HeadphonesIcon, Calculator, CheckCircle } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: <Home className="w-12 h-12" />,
      title: "Residential Solar Installation",
      description: "Custom solar solutions for your home. Reduce your electricity bills and increase property value with our expertly installed residential solar systems.",
      features: [
        "Free site assessment",
        "Custom system design",
        "Professional installation",
        "25-year warranty",
        "Monitoring system included"
      ],
      image: "https://images.unsplash.com/photo-1660330589505-9a433a742a7b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxzb2xhciUyMGluc3RhbGxhdGlvbiUyMHRlY2huaWNpYW4lMjB3b3JraW5nfGVufDB8fHx8MTc3MTM5MjkzM3ww&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <Building2 className="w-12 h-12" />,
      title: "Commercial Solar Solutions",
      description: "Scale your business sustainably. Our commercial solar installations help businesses reduce operational costs and demonstrate environmental responsibility.",
      features: [
        "ROI analysis",
        "Large-scale installations",
        "Minimal business disruption",
      ],
      image: "https://images.unsplash.com/photo-1624397640148-949b1732bb0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxzb2xhciUyMGluc3RhbGxhdGlvbiUyMHRlY2huaWNpYW4lMjB3b3JraW5nfGVufDB8fHx8MTc3MTM5MjkzM3ww&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <Wrench className="w-12 h-12" />,
      title: "Maintenance & Repair",
      description: "Keep your system running at peak performance. Our maintenance services ensure your solar investment continues to deliver maximum returns.",
      features: [
        "Annual inspections",
        "Panel cleaning",
        "System optimization",
        "Emergency repairs",
        "Performance monitoring"
      ],
      image: "https://images.unsplash.com/photo-1591710369924-0dcd50436306?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVscyUyMG1vZGVybiUyMGhvdXNlJTIwcm9vZiUyMHN1bm55fGVufDB8fHx8MTc3MTM5MjkzMHww&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <HeadphonesIcon className="w-12 h-12" />,
      title: "Solar Consulting",
      description: "Expert guidance for your solar journey. From feasibility studies to system optimization, our consultants provide comprehensive solar energy advice.",
      features: [
        "Energy audit",
        "Financial analysis",
        "Bank Fianance",
        "Solar Subsidy",
        "Post-installation support"
      ],
      image: "https://images.unsplash.com/photo-1720610784599-18c02b1cc9ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxzb2xhciUyMHBhbmVscyUyMG1vZGVybiUyMGhvdXNlJTIwcm9vZiUyMHN1bm55fGVufDB8fHx8MTc3MTM5MjkzMHww&ixlib=rb-4.1.0&q=85"
    }
  ];

  return (
    <div className="min-h-screen" data-testid="services-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading font-bold text-5xl md:text-7xl text-primary mb-6 tracking-tight">Our Services</h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Comprehensive solar solutions tailored to your needs. From residential rooftops to commercial installations, we've got you covered.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-8" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
          {services.map((service, index) => (
            <div 
              key={index}
              data-testid={`service-${index}`}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 text-primary rounded-2xl mb-6">
                  {service.icon}
                </div>
                <h2 className="font-heading font-semibold text-3xl md:text-4xl text-primary mb-4">{service.title}</h2>
                <p className="text-lg text-muted-foreground mb-8 leading-relaxed">{service.description}</p>
                
                <ul className="space-y-3">
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0" />
                      <span className="text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="rounded-3xl overflow-hidden shadow-xl">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-96 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Calculator className="w-16 h-16 text-secondary mx-auto mb-6" />
          <h2 className="font-heading font-bold text-3xl md:text-5xl text-primary-foreground mb-6">Calculate Your Savings</h2>
          <p className="text-primary-foreground/90 text-lg mb-8 leading-relaxed">
            See how much you could save with solar energy. Our calculator provides instant estimates based on your location and energy usage.
          </p>
          <a 
            href="/calculator" 
            data-testid="services-calculator-link"
            className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105 shadow-lg inline-block"
          >
            Try Solar Calculator
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;